import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { FormProvider, useForm, useFormState, useWatch } from "react-hook-form";

import { IconCheckmark } from "@synthesis/shared-icons";
import { Button } from "@synthesis/shared-ui/Button";
import { RadioGroup } from "@synthesis/shared-ui/Radio";
import { setSnack } from "@synthesis/shared-ui/SnackbarCenter";
import { TextArea } from "@synthesis/shared-ui/TextArea";
import {
  useCreate7,
  useUpdate4,
  useUpdateHighlightArea,
  findByProjectIdQueryKey,
} from "@synthesis/shared-services/models/modelsDrawingsController/hooks";

import {
  DEFAULT_POSITIONING_HINT,
  PLANE_ONLY_CHANGE_HINT,
  SNACK,
  buildCreateDrawingPayload,
  buildDefineDrawingUpdatePayload,
  formatDefineChangeWarning,
  getCollectionSuccessTitle,
  getDebugMessage,
  getPopupTitle,
  getSubmitButtonLabel,
  hasDrawingPlaneChanged,
  hasSelectionAreaChanged,
  isWholePageCollection,
  toDrawingPlane,
  type AddToCollectionFormFields,
  type AddToCollectionPopupProps,
} from "./add-to-collection-popup.lib";
import { renderRadioItems, toDrawingRotationName } from "./modal-open-pdf.lib";

const successSnackIcon = <IconCheckmark className="size-5" />;

const AddToCollectionPopup = ({
  onClose,
  onCancel,
  onUpdated,
  fileId,
  pageNumber,
  selectionCoords,
  initialSelection,
  mode = "default",
  variant = "default",
  drawingId,
  initialName,
  initialPlane,
  rotation,
  projectId,
}: AddToCollectionPopupProps) => {
  const handleCancel = onCancel ?? onClose;
  const methods = useForm<AddToCollectionFormFields>({
    defaultValues: {
      name: initialName ?? "",
      plane: mode === "define" ? (initialPlane ?? "") : "",
    },
  });
  const { errors } = useFormState({ control: methods.control });
  const plane = useWatch({ control: methods.control, name: "plane" });
  const errorCount = Object.keys(errors).length;
  const queryClient = useQueryClient();
  const createDrawing = useCreate7();
  const updateDrawing = useUpdate4();
  const updateHighlightArea = useUpdateHighlightArea();

  const isDefineMode = mode === "define";
  const isPlaneOnly = variant === "planeOnly";

  const hasAreaChanged = useMemo(
    () => isDefineMode && hasSelectionAreaChanged(selectionCoords, initialSelection),
    [initialSelection, isDefineMode, selectionCoords],
  );

  const hasPlaneChanged = isDefineMode && hasDrawingPlaneChanged(plane, initialPlane);

  const isWholePage = isWholePageCollection(mode, selectionCoords);

  const finishSuccess = (name: string, targetProjectId: string) => {
    queryClient.invalidateQueries({ queryKey: findByProjectIdQueryKey({ path: { projectId: targetProjectId } }) });
    setSnack({
      title: getCollectionSuccessTitle(isWholePage, name),
      messageType: "info",
      icon: successSnackIcon,
    });
    onClose();
  };

  const finishUpdateSuccess = (targetProjectId: string, updatedPlane?: ReturnType<typeof toDrawingPlane>) => {
    queryClient.invalidateQueries({ queryKey: findByProjectIdQueryKey({ path: { projectId: targetProjectId } }) });
    setSnack({
      title: isPlaneOnly ? SNACK.planeUpdated : SNACK.areaUpdated,
      messageType: "info",
      icon: successSnackIcon,
    });
    if (updatedPlane) {
      onUpdated?.({ plane: updatedPlane });
    }
    onClose();
  };

  const onSubmit = async (data: AddToCollectionFormFields) => {
    if (isDefineMode) {
      if (!projectId) {
        setSnack({
          title: SNACK.projectNotFound,
          messageType: "error",
        });
        return;
      }
      if (!drawingId) {
        setSnack({
          title: SNACK.drawingNotFound,
          messageType: "error",
        });
        return;
      }

      const drawingPayload = buildDefineDrawingUpdatePayload({
        name: data.name,
        plane: data.plane,
        pageNumber,
        includeName: !isPlaneOnly,
      });

      if (isPlaneOnly) {
        if (!data.plane) {
          setSnack({
            title: SNACK.planeRequired,
            messageType: "error",
          });
          return;
        }

        try {
          await updateDrawing.mutateAsync({
            body: { drawingId, ...drawingPayload },
          });
          finishUpdateSuccess(projectId, toDrawingPlane(data.plane));
        } catch (error) {
          setSnack({
            title: getDebugMessage(error)?.includes("already exists") ? SNACK.fileNameExists : SNACK.planeUpdateFailed,
            messageType: "error",
          });
        }
        return;
      }

      if (!selectionCoords) {
        setSnack({
          title: SNACK.selectionNotFound,
          messageType: "error",
        });
        return;
      }

      try {
        await updateDrawing.mutateAsync({
          body: { drawingId, ...drawingPayload },
        });

        if (hasAreaChanged) {
          await updateHighlightArea.mutateAsync({
            path: { drawingId },
            body: {
              originX: selectionCoords.originX,
              originY: selectionCoords.originY,
              areaWidth: selectionCoords.areaWidth,
              areaHeight: selectionCoords.areaHeight,
              rotation: toDrawingRotationName(rotation),
            },
          });
        }

        finishUpdateSuccess(projectId, toDrawingPlane(data.plane));
      } catch (error) {
        setSnack({
          title: getDebugMessage(error)?.includes("already exists") ? SNACK.fileNameExists : SNACK.drawingUpdateFailed,
          messageType: "error",
        });
      }
      return;
    }

    if (!data.name.trim()) {
      methods.setError("name", { message: "Обязательное поле" });
      return;
    }

    if (!projectId) {
      setSnack({
        title: SNACK.projectNotFound,
        messageType: "error",
      });
      return;
    }

    const name = data.name.trim();
    const payload = buildCreateDrawingPayload({
      projectId,
      name,
      fileId,
      pageNumber,
      plane: data.plane,
      rotation,
    });

    // Author берётся из Bearer (как в Insomnia). X-User-Id не шлём — кастомный header ломает CORS preflight.
    createDrawing.mutate(
      { body: payload, headers: {} as { "X-User-Id": string } },
      {
        onSuccess: (created) => {
          if (!selectionCoords || !created.id) {
            finishSuccess(name, projectId);
            return;
          }

          updateHighlightArea.mutate(
            {
              path: { drawingId: created.id },
              body: {
                originX: selectionCoords.originX,
                originY: selectionCoords.originY,
                areaWidth: selectionCoords.areaWidth,
                areaHeight: selectionCoords.areaHeight,
                rotation: toDrawingRotationName(rotation),
              },
            },
            {
              onSuccess: () => finishSuccess(name, projectId),
              onError: () => {
                setSnack({
                  title: SNACK.highlightSaveFailed,
                  messageType: "error",
                });
                onClose();
              },
            },
          );
        },
        onError: (error) => {
          const nameExists = getDebugMessage(error)?.includes("already exists");
          const addFailed = isWholePage ? SNACK.pageAddFailed : SNACK.areaAddFailed;

          setSnack({
            title: nameExists ? SNACK.fileNameExists : addFailed,
            messageType: "error",
          });
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <div className="absolute inset-0 z-10 flex items-center justify-center" onClick={handleCancel}>
        <div className="w-[400px] rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <h3 className="subtitle-lg mb-4">{getPopupTitle(variant)}</h3>
            <div className="flex flex-col gap-4">
              {!isPlaneOnly ? (
                <div className="flex flex-col gap-2">
                  <span>
                    Название <span className="text-red-600">*</span>
                  </span>
                  <TextArea key={errorCount} name="name" placeholder="Введите название" />
                </div>
              ) : null}
              <span>Плоскость</span>
              <RadioGroup name="plane" renderRadioGroupElements={renderRadioItems} />
              {!isPlaneOnly && !isDefineMode ? (
                <span className="text-type-minor subtitle-xs bg-neutrals-99 rounded-md px-4 py-3">{DEFAULT_POSITIONING_HINT}</span>
              ) : null}
              {!isPlaneOnly && isDefineMode && (hasAreaChanged || hasPlaneChanged) ? (
                <span className="text-type-minor subtitle-xs bg-neutrals-99 rounded-md px-4 py-3">
                  {formatDefineChangeWarning(hasAreaChanged, hasPlaneChanged)}
                </span>
              ) : null}
              {isPlaneOnly && hasPlaneChanged ? (
                <span className="text-type-minor subtitle-xs bg-neutrals-99 rounded-md px-4 py-3">{PLANE_ONLY_CHANGE_HINT}</span>
              ) : null}
              <div className="flex items-center justify-between">
                <Button size="sm" theme="primary" type="submit">
                  {getSubmitButtonLabel(mode, variant)}
                </Button>
                <Button size="sm" type="button" onClick={handleCancel}>
                  Отменить
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </FormProvider>
  );
};

export default AddToCollectionPopup;
